/* eslint-disable max-len */
import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { deepSetObject, digObject } from '@domain-group/javascript-utils'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import CampaignQuoteTemplateSectionSearch from '@components/CampaignQuoteTemplateSectionSearch'
import ProviderSubHeader from '@components/ProviderSubHeader'

import { confirmDelete } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useDefaultQuoteItem, { useDefaultQuoteItemForm } from '@hooks/useDefaultQuoteItem'

import type { CampaignQuoteTemplateSectionModel } from '@models/types'

const MODAL_KEY = 'CreateOrEditDefaultQuoteItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  createDefaultQuoteItem: (payload: HandleCallbackActionParams) => void,
  deleteDefaultQuoteItem: (payload: HandleCallbackActionParams) => void,
  updateDefaultQuoteItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditDefaultQuoteItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createDefaultQuoteItem, updateDefaultQuoteItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const defaultQuoteItem = digObject(modalPayload, 'defaultQuoteItem', {})

  const newRecord = !defaultQuoteItem.id

  const {
    callbacks: {
      createDefaultQuoteItem: createFn,
      deleteDefaultQuoteItem: deleteFn,
      updateDefaultQuoteItem: updateFn,
    },
    creating,
    deleting,
    isOrderableDefaultQuoteItem,
    provider,
    updating,
  } = useDefaultQuoteItem(defaultQuoteItem)

  // @ts-expect-error TS2339
  const shouldRenderProviderTile = !newRecord && provider?.id

  const customRequiredFields = isOrderableDefaultQuoteItem ? [{ key: 'display_title' }] : []

  const {
    entityState,
    entityState: {
      allow_toggle,
      data,
      data: {
        section_title,
      },
      description,
      display_title,
      internal_notes,
      title,
      value_type,
      value,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useDefaultQuoteItemForm(defaultQuoteItem, { validateOn: 'change', customRequiredFields })

  const createOrUpdateDefaultQuoteItemPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Default Quote Item ${newRecord ? 'Created' : 'Updated'}`,
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: {
      deleteString: 'remove',
      entityName: 'Default Quote Item',
    },
  }

  const renderTitleField = () => {
    const inputErrors = isOrderableDefaultQuoteItem ? errors.display_title : errors.title
    const inputName = isOrderableDefaultQuoteItem ? 'display_title' : 'title'
    const inputValue = isOrderableDefaultQuoteItem ? display_title : title

    return (
      <Form.Field label="* Title" errorMessage={inputErrors}>
        <input name={inputName} type="text" value={inputValue} {...handlers} />
      </Form.Field>
    )
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Default Quote Item"
      />

      {shouldRenderProviderTile && (
        // @ts-expect-error TS2339
        <ProviderSubHeader providerId={provider?.id} secondaryText={`Quote Item #${defaultQuoteItem?.id}`} />
      )}

      <SidebarModal.Content hasSubHeader={shouldRenderProviderTile}>
        <Form>
          {renderTitleField()}

          <Form.Field label="Search Template Section" marginTop="large">
            <CampaignQuoteTemplateSectionSearch
              callbacks={{
                onChange: (value: string) => setEntityState({ data: deepSetObject(data, 'section_title', value) }),
                selectItem: (item: CampaignQuoteTemplateSectionModel) => setEntityState({ data: deepSetObject(data, 'section_title', item.title) }),
              }}
              value={section_title}
            />
          </Form.Field>

          <Form.Field label="* Value" errorMessage={errors.value} marginTop="large">
            <input data-value-type="number" disabled={isOrderableDefaultQuoteItem} name="value" type="number" value={value} {...handlers} />
          </Form.Field>

          <Form.Field label="Value Type" errorMessage={errors.value_type} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select disabled={isOrderableDefaultQuoteItem} name="value_type" value={value_type} onChange={handlers.onChange}>
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </select>
          </Form.Field>

          <Form.Field label="Item Description" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="description"
              placeholder="Visible on Quote"
              value={description || ''}
              style={{ height: 80, resize: 'vertical' }}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Internal Notes" errorMessage={errors.internal_notes} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <textarea
              name="internal_notes"
              placeholder="Hidden on Quote"
              value={internal_notes || ''}
              style={{ height: 80 }}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Allow Toggle" errorMessage={errors.allow_toggle} marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="boolean" name="allow_toggle" value={allow_toggle} {...handlers}>
              <option value="false">Locked</option>
              <option value="true">Can Toggle</option>
            </select>
          </Form.Field>
        </Form>

        {!newRecord && (
          <Form.Field label="More Options" marginTop="large">
            <Button
              buttonStyle="secondaryUtility"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              loading={deleting}
              // @ts-expect-error TS2345
              onClick={() => confirmDelete(confirmDeletePayload)}
              size="medium"
            >
              Delete Item
            </Button>
          </Form.Field>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createDefaultQuoteItem(createOrUpdateDefaultQuoteItemPayload)
              : () => updateDefaultQuoteItem(createOrUpdateDefaultQuoteItemPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Default Quote Item` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditDefaultQuoteItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
